import { useState } from "react";
import { Crown, Send, Mic, Heart, Brain, Flame, Compass, Award } from "lucide-react";
import { api, BRAND } from "../lib/api";
import { toast } from "sonner";

const TOPICS = [
  { icon: Heart, t: "Healing & Wholeness", d: "Recovering from trauma, grief, and the wounds of yesterday." },
  { icon: Compass, t: "Faith & Purpose", d: "Discovering your God-given assignment in this season." },
  { icon: Brain, t: "Mindset Mastery", d: "Renewing the mind, breaking limiting beliefs, walking in identity." },
  { icon: Flame, t: "Surrender & Breakthrough", d: "What it means to lay it down and step into freedom." },
  { icon: Crown, t: "Kingdom Leadership", d: "Leading from the overflow — at home, at work, in ministry." },
  { icon: Award, t: "Women's Empowerment", d: "Healing crowns: confidence, clarity, and royal identity." },
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "",
    event_type: "Speaking Engagement", event_date: "",
    budget: "", location: "", message: ""
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete the required fields");
    setBusy(true);
    try {
      await api.post("/bookings", form);
      toast.success("Inquiry sent. We'll be in touch within 1–2 business days.");
      setForm({ name:"", email:"", phone:"", organization:"", event_type:"Speaking Engagement", event_date:"", budget:"", location:"", message:"" });
    } catch {
      toast.error("Could not submit right now.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-booking">
      <section className="container-narrow pt-16 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="label-luxe">Booking · Speaking · Coaching</div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mt-4">
              Book <span className="italic gold-text">Pastor Shalonda</span> Raymond.
            </h1>
            <p className="mt-6 text-white text-lg max-w-2xl leading-relaxed">
              Conferences. Sunday services. Coaching cohorts. Women's gatherings.
              Pastor Shalonda brings a Spirit-led, no-jargon, transformational word that meets
              your audience exactly where they are.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="card-luxe">
              <Mic className="w-8 h-8 text-[#D91668]" />
              <div className="mt-3 font-serif text-2xl">Need a quick answer?</div>
              <div className="text-white/80 text-sm mt-2">Email or call directly:</div>
              <a href={`mailto:${BRAND.email}`} className="block mt-3 text-[#ECC23D] font-semibold hover:underline">{BRAND.email}</a>
              <a href={`tel:${BRAND.phone.replace(/\D/g,"")}`} className="block mt-1 text-[#ECC23D] font-semibold hover:underline">{BRAND.phone}</a>
            </div>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="container-narrow py-12">
        <div className="label-luxe">Speaking Topics</div>
        <h2 className="font-serif text-3xl mt-3 mb-8">Where the anointing flows.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOPICS.map((x,i) => (
            <div key={i} className="card-luxe">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#ECC23D] flex items-center justify-center"><x.icon className="w-6 h-6"/></div>
              <h3 className="font-serif text-xl mt-4">{x.t}</h3>
              <p className="text-white/90 text-sm mt-2 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BOOKING FORM */}
      <section className="container-narrow py-16">
        <div className="rounded-[32px] glass-strong p-8 md:p-12">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="label-luxe">Inquiry Form</div>
              <h2 className="font-serif text-3xl mt-3">Tell us about your event.</h2>
              <p className="mt-4 text-white/90 leading-relaxed text-sm">
                The more details, the better we can serve you. Pastor Shalonda's team reviews every inquiry personally.
              </p>
            </div>
            <form onSubmit={submit} className="md:col-span-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-luxe">Your Name *</label>
                  <input data-testid="booking-name" required value={form.name} onChange={update("name")} className="input-luxe mt-2" />
                </div>
                <div>
                  <label className="label-luxe">Email *</label>
                  <input data-testid="booking-email" type="email" required value={form.email} onChange={update("email")} className="input-luxe mt-2" />
                </div>
                <div>
                  <label className="label-luxe">Phone</label>
                  <input data-testid="booking-phone" value={form.phone} onChange={update("phone")} className="input-luxe mt-2" />
                </div>
                <div>
                  <label className="label-luxe">Organization / Church</label>
                  <input data-testid="booking-org" value={form.organization} onChange={update("organization")} className="input-luxe mt-2" />
                </div>
                <div>
                  <label className="label-luxe">Event Type</label>
                  <select data-testid="booking-event-type" value={form.event_type} onChange={update("event_type")} className="input-luxe mt-2">
                    <option>Speaking Engagement</option>
                    <option>Ministry Event</option>
                    <option>Conference</option>
                    <option>Coaching Session</option>
                    <option>Women's Gathering</option>
                    <option>Podcast / Interview</option>
                  </select>
                </div>
                <div>
                  <label className="label-luxe">Event Date</label>
                  <input data-testid="booking-date" type="date" value={form.event_date} onChange={update("event_date")} className="input-luxe mt-2" />
                </div>
                <div>
                  <label className="label-luxe">Location / Format</label>
                  <input data-testid="booking-location" value={form.location} onChange={update("location")} className="input-luxe mt-2" placeholder="In-person city or Virtual" />
                </div>
                <div>
                  <label className="label-luxe">Budget</label>
                  <input data-testid="booking-budget" value={form.budget} onChange={update("budget")} className="input-luxe mt-2" placeholder="$ range or honorarium" />
                </div>
              </div>
              <div>
                <label className="label-luxe">Tell us about the audience & what you need *</label>
                <textarea data-testid="booking-message" required rows={5} value={form.message} onChange={update("message")} className="input-luxe mt-2" />
              </div>
              <button data-testid="booking-submit" disabled={busy} className="btn-fuchsia w-full sm:w-auto">
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Submit Inquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
